import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';

import EnvLabel from 'components/EnvLabel';
import Auth from 'services/Auth';
import logo from 'assets/img/logo.png';
import verifiedEmailIcon from 'assets/img/verified-email-icon.svg';
import unverifiedEmailIcon from 'assets/img/unverified-email-icon.svg';
import './VerifyEmailPage.scss';

const VerifyEmailPage = () => {
  const query = new URLSearchParams(useLocation().search);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token] = useState(() => query.get('token'));
  const { t } = useTranslation();

  useEffect(() => {
    if (!token) {
      setStatus('VERIFICATION_ERROR');
      setLoading(false);
      return;
    }

    Auth.verify(token)
      .then((data) => setStatus(data.status))
      .catch(() => setStatus('VERIFICATION_ERROR'))
      .finally(() => setLoading(false));
  }, [token]);

  const renderMessage = () => {
    switch (status) {
      case 'SUCCESS':
        return {
          iconColor: 'green',
          icon: 'verified',
          title: t('verify.successTitle'),
          description: t('verify.successDescription'),
        };
      case 'TOKEN_ALREADY_USED':
        return {
          iconColor: 'green',
          icon: 'verified',
          title: t('verify.alreadyVerifiedTitle'),
          description: '',
        };
      case 'TOKEN_EXPIRED':
        return {
          iconColor: 'red',
          icon: 'unverified',
          title: t('verify.expiredTitle'),
          description: (
            <>
              <p>{t('verify.expiredStepsIntro')}</p>
              <ol>
                <li>
                  <Trans i18nKey="verify.expiredStep1">
                    Go to <strong>My Profile</strong> in your Sunrise account.
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="verify.expiredStep2">
                    Find the account recovery section and click "Resend
                    verification email".
                  </Trans>
                </li>
              </ol>
            </>
          ),
        };
      case 'TOKEN_INVALID':
      case 'VERIFICATION_ERROR':
      default:
        return {
          iconColor: 'red',
          icon: 'unverified',
          title: t('verify.errorTitle'),
          description: (
            <>
              <p>{t('verify.error1')}</p>
              <p>{t('verify.error2')}</p>
              <p>
                <Trans i18nKey="verify.error3">
                  If the problem continues, go to <strong>My Profile</strong> to
                  resend a new verification link.
                </Trans>
              </p>
            </>
          ),
        };
    }
  };

  const { iconColor, icon, title, description } = renderMessage();

  return (
    <Dialog fullScreen open>
      <div className="verify-email">
        <EnvLabel isFixed={true} styles={{ zIndex: 1, top: '10px' }} />
        <img
          src={logo}
          className="logo"
          width="118"
          height="32"
          alt="Aviapartner"
        />
        <div className="container">
          {loading ? (
            <p>{t('verify.verifying')}</p>
          ) : (
            <>
              <div className={`status-icon ${iconColor}`}>
                <img
                  src={
                    icon === 'verified'
                      ? verifiedEmailIcon
                      : unverifiedEmailIcon
                  }
                  alt="status"
                />
              </div>
              <h2 className={`title ${iconColor}`}>{title}</h2>
              <div className="description">{description}</div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

VerifyEmailPage.propTypes = {
  open: PropTypes.bool,
};

export default VerifyEmailPage;
